import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] active:transition-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        gradient:
          "bg-primary text-primary-foreground hover:bg-primary-hover",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Marks the button busy: forces `disabled`, sets `aria-busy`, and swaps its
   * leading icon for a spinner. Has no effect when `asChild` is set — Slot
   * requires exactly one child element, so injecting a spinner next to it
   * would break that contract. `asChild` callers keep managing their own busy
   * state; the existing `{busy ? <Spinner/> : <Icon/>}` call sites are
   * untouched and stay exactly as they are.
   */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
    if (asChild) {
      // `disabled` stays inside `...props` here (not destructured out above)
      // and flows through the spread exactly as before this prop existed.
      // Slot's prop type doesn't declare `disabled` as a named attribute, so
      // passing it explicitly (`disabled={disabled}`) is a TS2322 under
      // `tsc --noEmit` even though it works at runtime; the spread isn't
      // excess-property-checked the same way, so it stays type-clean.
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }

    // Only pulled out here, for the real <button> element, which does declare
    // `disabled` — combining it with `loading` is safe on this branch.
    const { disabled, ...buttonProps } = props

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...buttonProps}
      >
        {loading && (
          <Loader2
            className="h-4 w-4 shrink-0 animate-spin"
            aria-hidden="true"
            data-testid="button-spinner"
          />
        )}
        {/* `contents` keeps this wrapper out of the flex layout (gap/alignment
            behave exactly as without it); scoping the icon-hide to it instead
            of the whole button keeps the spinner above from hiding itself. */}
        <span className={cn("contents", loading && "[&_svg]:hidden")}>{children}</span>
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
